import { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import OfferCard from '@/components/cards/OfferCard';
import ParchmentCard from '@/components/ui/ParchmentCard';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import * as partnerService from '@/services/partnerService';
import * as playerService from '@/services/playerService';
import useAuthStore from '@/store/authStore';

const filters = [
  { value: 'all', label: 'Todas' },
  { value: 'available', label: 'Disponíveis' },
  { value: 'pending', label: 'Meus QR' },
];

const normalizeError = (err) => {
  const message = err.response?.data?.message || 'Não foi possível concluir a ação.';
  if (message.includes('pending redemption')) return 'Já tens um QR pendente para esta oferta.';
  if (message.includes('Not enough coins')) return 'Moedas insuficientes para esta oferta.';
  if (message.includes('out of stock')) return 'Esta oferta está esgotada.';
  return message;
};

const formatDateTime = (date) => date
  ? new Date(date).toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : 'Sem validade';

export default function ShopPage() {
  const [offers, setOffers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [filter, setFilter] = useState('available');
  const [loading, setLoading] = useState(true);
  const [redeemingId, setRedeemingId] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const updatePlayer = useAuthStore((state) => state.updatePlayer);
  const player = useAuthStore((state) => state.player);

  const pendingRedemptions = useMemo(
    () => redemptions.filter((item) => item.status === 'pending'),
    [redemptions]
  );

  const visibleOffers = useMemo(() => {
    if (filter === 'pending') return [];
    const now = new Date();
    return offers.filter((offer) => {
      if (filter !== 'available') return true;
      const expired = offer.expiresAt && new Date(offer.expiresAt) < now;
      return !expired && offer.stock !== 0;
    });
  }, [filter, offers]);

  const loadShop = async () => {
    setLoading(true);
    setError('');
    try {
      const [offersRes, redemptionsRes] = await Promise.all([
        partnerService.getOffers(),
        partnerService.getMyRedemptions(),
      ]);
      setOffers(offersRes.data.data || []);
      setRedemptions(redemptionsRes.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar as ofertas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShop();
  }, []);

  const redeemOffer = async (offer) => {
    setRedeemingId(offer.id);
    setError('');
    setNotice('');
    try {
      const res = await partnerService.redeemOffer(offer.id);
      const data = res.data.data;
      setQrData(data);
      setNotice('QR criado. As moedas só são cobradas quando a loja validar.');

      const profileRes = await playerService.getProfile();
      updatePlayer(profileRes.data.data);
      await loadShop();
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setRedeemingId(null);
    }
  };

  const openPendingQR = (redemption) => {
    setQrData({
      redemptionId: redemption.id,
      qrToken: redemption.qrCode,
      expiresAt: redemption.expiresAt,
      offer: {
        title: redemption.offer?.title || 'Oferta resgatada',
        partner: redemption.offer?.partner?.name || 'Parceiro',
      },
    });
  };

  const cancelPendingQR = async (redemption) => {
    setCancellingId(redemption.id);
    setError('');
    setNotice('');
    try {
      const res = await partnerService.cancelRedemption(redemption.id);
      const refunded = res.data.data?.refundedCoins || 0;
      setNotice(refunded > 0 ? `QR cancelado. ${refunded} moedas devolvidas.` : 'QR cancelado.');
      const profileRes = await playerService.getProfile();
      updatePlayer(profileRes.data.data);
      await loadShop();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível cancelar este QR.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <main className="h-full overflow-y-auto bg-background px-4 pb-4 pt-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-muted">Parceiros locais</p>
          <h1 className="font-serif text-2xl font-black text-accent">Ofertas</h1>
        </div>
        <div className="rounded-full border border-edge bg-surfaceAlt/70 px-3 py-2 text-right">
          <strong className="block font-serif text-base leading-none text-accent">{player?.coins || 0}</strong>
          <span className="text-[10px] font-black uppercase text-muted">moedas</span>
        </div>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {filters.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setFilter(item.value)}
            className={`flex-shrink-0 rounded-full border px-3 py-2 text-xs font-black transition ${
              filter === item.value
                ? 'border-accent bg-accent text-white'
                : 'border-edge bg-surface text-muted'
            }`}
          >
            {item.label}
            {item.value === 'pending' && pendingRedemptions.length > 0 ? ` ${pendingRedemptions.length}` : ''}
          </button>
        ))}
      </div>

      {notice && <ParchmentCard className="mt-3 py-3 text-sm font-bold text-forest-light">{notice}</ParchmentCard>}
      {error && <ParchmentCard className="mt-3 py-3 text-sm font-bold text-pirate-red">{error}</ParchmentCard>}

      {filter === 'pending' ? (
        <section className="mt-4 grid gap-3">
          {pendingRedemptions.map((item) => (
            <ParchmentCard key={item.id} className="text-fg">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-base font-black">{item.offer?.title || 'Oferta resgatada'}</h2>
                  <p className="mt-1 text-xs font-bold text-fg/60">{item.offer?.partner?.name || 'Parceiro'} · expira {formatDateTime(item.expiresAt)}</p>
                </div>
                <span className="rounded-full bg-forest/15 px-2 py-1 text-[10px] font-black uppercase text-forest-light">QR ativo</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button variant="green" className="rounded-lg px-3 py-2 text-xs" onClick={() => openPendingQR(item)}>
                  Ver QR
                </Button>
                <Button
                  variant="ghost"
                  className="rounded-lg px-3 py-2 text-xs text-pirate-red"
                  disabled={cancellingId === item.id}
                  onClick={() => cancelPendingQR(item)}
                >
                  Cancelar
                </Button>
              </div>
            </ParchmentCard>
          ))}
          {!loading && pendingRedemptions.length === 0 && (
            <ParchmentCard className="text-sm font-bold text-fg/70">Ainda não tens QR pendentes.</ParchmentCard>
          )}
        </section>
      ) : (
        <section className="mt-4 grid grid-cols-1 gap-4">
          {visibleOffers.map((offer) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              onRedeem={redeemOffer}
              disabled={redeemingId === offer.id}
            />
          ))}
          {loading && <ParchmentCard className="text-sm font-bold text-fg/70">A carregar ofertas...</ParchmentCard>}
          {!loading && visibleOffers.length === 0 && (
            <ParchmentCard className="text-sm font-bold text-fg/70">Não há ofertas nesta vista.</ParchmentCard>
          )}
        </section>
      )}

      <Modal isOpen={Boolean(qrData)} onClose={() => setQrData(null)}>
        <ParchmentCard className="text-center text-fg">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-accent">Resgate ativo</p>
          <h2 className="mt-1 font-serif text-xl font-black">{qrData?.offer?.title}</h2>
          <p className="mt-1 text-sm font-bold text-fg/60">{qrData?.offer?.partner}</p>
          {qrData?.qrCodeImage && (
            <img src={qrData.qrCodeImage} alt="QR code da oferta" className="mx-auto mt-4 h-52 w-52 rounded-xl border border-edge bg-white p-2" />
          )}
          {!qrData?.qrCodeImage && qrData?.qrToken && (
            <div className="mx-auto mt-4 flex h-52 w-52 items-center justify-center rounded-xl border border-edge bg-white p-2">
              <QRCodeSVG value={qrData.qrToken} size={188} level="H" />
            </div>
          )}
          <p className="mt-3 text-xs font-bold text-fg/60">Válido até {formatDateTime(qrData?.expiresAt)}</p>
          <p className="mt-2 text-xs font-bold text-fg/60">As moedas são cobradas apenas quando a loja validar este QR.</p>
          <Button variant="gold" fullWidth className="mt-4" onClick={() => setQrData(null)}>Fechar</Button>
        </ParchmentCard>
      </Modal>
    </main>
  );
}
